/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `sideMenuUtils` for the Caintra Connect frontend.
 * @module components/SideMenu/sideMenuUtils
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import {
  BREAKPOINTS,
  COLLAPSED_MENU_WIDTH,
  CONTEXTS,
  ROUTES,
} from "./constants";

/** Returns true when the viewport width corresponds to a mobile layout. */
export const isMobileViewport = (width) => width <= BREAKPOINTS.MOBILE_MAX;

/** Returns true when the viewport width corresponds to a tablet layout. */
export const isTabletViewport = (width) =>
  width >= BREAKPOINTS.TABLET_MIN && width <= BREAKPOINTS.TABLET_MAX;

/**
 * Computes the CSS width value for the sidebar based on viewport and collapse state.
 * Returns "0px" for mobile viewports where the sidebar is hidden.
 */
export const getSideMenuWidth = (
  isCollapsedState,
  width = window.innerWidth,
) => {
  if (width >= BREAKPOINTS.TABLET_MIN && width <= BREAKPOINTS.TABLET_MAX) {
    return isCollapsedState ? COLLAPSED_MENU_WIDTH : "25%";
  }
  if (
    width >= BREAKPOINTS.SMALL_DESKTOP_MIN &&
    width <= BREAKPOINTS.LARGE_DESKTOP_MAX
  ) {
    return isCollapsedState ? COLLAPSED_MENU_WIDTH : "18%";
  }
  if (width >= BREAKPOINTS.XL_MIN) {
    return isCollapsedState ? COLLAPSED_MENU_WIDTH : "14%";
  }
  return "0px";
};

/** Updates the global CSS variable consumed by content sections aligned to the sidebar. */
export const setSideMenuWidthVar = (isCollapsedState) => {
  document.documentElement.style.setProperty(
    "--side-menu-width",
    getSideMenuWidth(isCollapsedState),
  );
};

/**
 * Returns true when the current path belongs to the "Personalizar evento" section.
 * Keeps the submenu expanded while navigating within that section.
 */
export const isCustomizeEventRoute = (pathname) =>
  pathname === ROUTES.MANAGE_EVENTS ||
  pathname === ROUTES.ADD_EVENT ||
  pathname.startsWith(ROUTES.EVENT_EDIT) ||
  pathname === ROUTES.BUSINESS_EVENTS ||
  pathname === ROUTES.BUSINESS_EVENT_FORM ||
  pathname === ROUTES.VIEW_APPOINTMENTS ||
  pathname === ROUTES.APPOINTMENT_REQUEST ||
  pathname === ROUTES.B2B_EVENTS ||
  pathname === ROUTES.B2B_EVENT_FORM ||
  /^\/b2bEvent\/[^/]+\/virtual-line-companies$/.test(pathname) ||
  pathname === ROUTES.VIRTUAL_EVENTS ||
  pathname === ROUTES.VIRTUAL_EVENT_FORM ||
  pathname === ROUTES.BREAKFAST_EVENTS ||
  pathname === ROUTES.BREAKFAST_EVENT_FORM;

/** Safely checks whether a permission flag is enabled in the user's permission list. */
export const hasPermission = (permissions, key) =>
  permissions?.find((permission) =>
    Object.prototype.hasOwnProperty.call(permission, key),
  )?.[key];

const isCompanyProfileActive = (path, context, pathname, navContext) =>
  path === ROUTES.COMPANY_PROFILE &&
  context === CONTEXTS.MY_COMPANY &&
  (pathname === ROUTES.COMPANY_PROFILE ||
    pathname.startsWith(ROUTES.COMPANY_EDIT)) &&
  navContext === CONTEXTS.MY_COMPANY;

const isCompanyCatalogActive = (path, context, pathname, navContext) =>
  path === ROUTES.COMPANY_CATALOG &&
  context === CONTEXTS.COMPANY_CATALOG &&
  (pathname === ROUTES.COMPANY_CATALOG ||
    (pathname.startsWith(ROUTES.COMPANY_EDIT) &&
      navContext === CONTEXTS.COMPANY_CATALOG));

const isDirectoryActive = (path, context, pathname, navContext) =>
  path === ROUTES.DIRECTORY &&
  context === CONTEXTS.DIRECTORY &&
  (pathname === ROUTES.DIRECTORY ||
    (pathname === ROUTES.COMPANY_PROFILE &&
      navContext === CONTEXTS.DIRECTORY) ||
    (pathname.startsWith(ROUTES.COMPANY_EDIT) &&
      navContext === CONTEXTS.DIRECTORY));

const isBuyerCompaniesActive = (path, context, pathname, navContext) =>
  path === ROUTES.BUYER_COMPANIES &&
  context === CONTEXTS.BUYER_COMPANIES &&
  (pathname === ROUTES.BUYER_COMPANIES ||
    (pathname === ROUTES.BUYER_COMPANY_FORM &&
      navContext === CONTEXTS.BUYER_COMPANIES));

const isEventsActive = (path, pathname) => {
  if (path !== ROUTES.EVENTS) {
    return false;
  }
  const onB2bVirtualLineCompaniesHub =
    /^\/b2bEvent\/[^/]+\/virtual-line-companies$/.test(pathname);
  const onEventPreRegister = /^\/events\/[^/]+\/pre-register$/.test(pathname);
  return (
    pathname === ROUTES.EVENTS ||
    onEventPreRegister ||
    (pathname.startsWith(ROUTES.B2B_EVENT_DETAIL_PREFIX) &&
      !onB2bVirtualLineCompaniesHub) ||
    pathname.startsWith("/businessEvent/")
  );
};

const isManageEventsActive = (path, pathname) =>
  path === ROUTES.MANAGE_EVENTS &&
  (pathname === ROUTES.MANAGE_EVENTS ||
    pathname === ROUTES.ADD_EVENT ||
    pathname.startsWith(ROUTES.EVENT_EDIT));

const isBusinessEventsActive = (path, pathname) =>
  path === ROUTES.BUSINESS_EVENTS &&
  (pathname === ROUTES.BUSINESS_EVENTS ||
    pathname === ROUTES.BUSINESS_EVENT_FORM ||
    pathname === ROUTES.VIEW_APPOINTMENTS ||
    pathname === ROUTES.APPOINTMENT_REQUEST);

const isB2bEventsActive = (path, pathname) =>
  path === ROUTES.B2B_EVENTS &&
  (pathname === ROUTES.B2B_EVENTS ||
    pathname === ROUTES.B2B_EVENT_FORM ||
    pathname === "/b2bEventUsersList" ||
    /^\/b2bEvent\/[^/]+\/virtual-line-companies$/.test(pathname));

const isVirtualEventsActive = (path, pathname) =>
  path === ROUTES.VIRTUAL_EVENTS &&
  (pathname === ROUTES.VIRTUAL_EVENTS ||
    pathname === ROUTES.VIRTUAL_EVENT_FORM);

const isBreakfastEventsActive = (path, pathname) =>
  path === ROUTES.BREAKFAST_EVENTS &&
  (pathname === ROUTES.BREAKFAST_EVENTS ||
    pathname === ROUTES.BREAKFAST_EVENT_FORM);

const isDefaultRouteActive = (path, context, pathname, navContext) => {
  if (path !== pathname) {
    return false;
  }
  if (context && navContext === context) {
    return true;
  }
  return !context;
};

/**
 * Determines whether a sidebar item should be highlighted for the current route.
 * Delegates to focused helpers for routes with special matching rules.
 */
export const isMenuItemActive = (path, context, location, navigationState) => {
  const pathname = location.pathname;
  const navContext = navigationState.context;

  if (isCompanyProfileActive(path, context, pathname, navContext)) {
    return true;
  }
  if (isCompanyCatalogActive(path, context, pathname, navContext)) {
    return true;
  }
  if (isDirectoryActive(path, context, pathname, navContext)) {
    return true;
  }
  if (isBuyerCompaniesActive(path, context, pathname, navContext)) {
    return true;
  }
  if (isEventsActive(path, pathname)) {
    return true;
  }
  if (isManageEventsActive(path, pathname)) {
    return true;
  }
  if (isBusinessEventsActive(path, pathname)) {
    return true;
  }
  if (isB2bEventsActive(path, pathname)) {
    return true;
  }
  if (isVirtualEventsActive(path, pathname)) {
    return true;
  }
  if (isBreakfastEventsActive(path, pathname)) {
    return true;
  }
  return isDefaultRouteActive(path, context, pathname, navContext);
};

/** Returns true when the "Personalizar evento" parent item should appear active. */
export const isCustomizeEventSectionActive = (location, checkActive) =>
  checkActive(ROUTES.BUSINESS_EVENTS) ||
  checkActive(ROUTES.B2B_EVENTS) ||
  checkActive(ROUTES.VIRTUAL_EVENTS) ||
  checkActive(ROUTES.MANAGE_EVENTS) ||
  checkActive(ROUTES.BREAKFAST_EVENTS) ||
  /^\/b2bEvent\/[^/]+\/virtual-line-companies$/.test(location.pathname);
